// Pure adapter: backend SimEvent dicts (snake_case, from /api/match/*) → localized log lines,
// reusing the SAME i18n keys as the local controller. Wired in via JKGame_MatchController.
// backendAction (the backend-flag action path) — it reads the authoritative sim's snake_case
// JSON directly (att_hit/attacker/is_player) and handles the backend 'defend' kind natively, so
// no snake→camel reshaping is needed. Framework-free.

import { zoneName } from '../game/JKGame_assets'
import { t, boonName, itemName, partLabel, weaponName } from '../i18n'
import type { LogTone } from '../sim/JKGame_types'
import type { BackendState } from './JKGame_api'

export interface BackendEvent {
  k: string
  [key: string]: unknown
}
export interface BackendLogLine {
  text: string
  tone: LogTone
}

interface BEResult {
  attacker: string
  defender: string
  att_hit: boolean
  att_dmg: number
  att_crit: boolean
  att_injury: string | null
  countered: boolean
  def_dmg: number
  def_injury: string | null
  defender_died: boolean
  attacker_died: boolean
  key_taken: boolean
}

const part = partLabel // shared TS-enforced body-part label (i18n); no raw-key cast

// NOTE: localizeAtt/localizeDef MUST emit the same lines as MatchController.localizePlayerAttack/
// localizePlayerDefend (the local-sim twins) — same i18n keys, tones, order — differing ONLY in
// field case (snake here, camel there, since the two sims serialise the exchange result
// differently). Keep the pair in sync: a log line added to one belongs in the other.

// Player is the attacker (playerExchange).
function localizeAtt(r: BEResult): BackendLogLine[] {
  const out: BackendLogLine[] = []
  if (r.att_hit) {
    const base = t('log.attackHit', { name: r.defender, dmg: r.att_dmg })
    out.push({ text: r.att_crit ? `${t('log.crit')} ${base}` : base, tone: 'good' })
    if (r.att_injury) out.push({ text: t('log.injury', { who: r.defender, part: part(r.att_injury) }), tone: 'good' })
  } else {
    out.push({ text: t('log.attackMiss'), tone: 'normal' })
  }
  if (r.countered && r.def_dmg > 0) {
    out.push({ text: t('log.counterHit', { name: r.defender, dmg: r.def_dmg }), tone: 'bad' })
    if (r.def_injury) out.push({ text: t('log.injurySelf', { part: part(r.def_injury) }), tone: 'bad' })
  }
  return out
}

// Bot is the attacker, player defends (defend).
function localizeDef(r: BEResult): BackendLogLine[] {
  const out: BackendLogLine[] = []
  if (r.att_hit) {
    out.push({ text: t('log.enemyHit', { name: r.attacker, dmg: r.att_dmg }), tone: 'bad' })
    if (r.att_injury) out.push({ text: t('log.injurySelf', { part: part(r.att_injury) }), tone: 'bad' })
  } else {
    out.push({ text: t('log.enemyMiss', { name: r.attacker }), tone: 'normal' })
  }
  if (r.countered && r.def_dmg > 0) {
    out.push({ text: t('log.attackHit', { name: r.attacker, dmg: r.def_dmg }), tone: 'good' })
    if (r.def_injury) out.push({ text: t('log.injury', { who: r.attacker, part: part(r.def_injury) }), tone: 'good' })
  }
  return out
}

export function localizeBackendEvent(ev: BackendEvent): BackendLogLine[] {
  const s = (k: string): string => String(ev[k] ?? '')
  const num = (k: string): number => Number(ev[k] ?? 0)
  switch (ev.k) {
    case 'move':
      return [{ text: t('log.move', { zone: zoneName(num('zone')) }), tone: 'normal' }]
    case 'restSp':
      // "Already full" (n≤0) reads better than "recovered 0" — parity with the local sim.
      return [{ text: num('n') > 0 ? t('log.rest.sp', { n: num('n') }) : t('log.rest.spFull'), tone: 'normal' }]
    case 'restHp':
      return [{ text: num('n') > 0 ? t('log.rest.hp', { n: num('n') }) : t('log.rest.hpFull'), tone: 'normal' }]
    case 'searchWeapon':
      return [{ text: t('log.search.weapon', { item: weaponName(s('item')) }), tone: 'good' }]
    case 'searchItem':
      return [{ text: t('log.search.item', { item: itemName(s('item')) }), tone: 'good' }]
    case 'searchNothing':
      return [{ text: t('log.search.nothing'), tone: 'normal' }]
    case 'hazard':
      return [{ text: t('log.hazard', { n: num('dmg') }), tone: 'bad' }]
    case 'botKill':
      return [{ text: t('log.botKill', { killer: s('killer'), victim: s('victim') }), tone: 'system' }]
    case 'collar':
      return [{ text: t('log.collar', { name: s('name') }), tone: (ev.is_player as boolean) ? 'bad' : 'system' }]
    case 'keyLost':
      return [{ text: t('log.keyLost', { name: s('name') }), tone: 'system' }]
    case 'broadcast': {
      const zones = ((ev.zones as number[] | undefined) ?? []).map((z) => zoneName(z)).join('、')
      return [{ text: t('log.broadcast', { zones }), tone: 'system' }]
    }
    case 'youKilled':
      return [{ text: t('log.youKilled', { name: s('name') }), tone: 'good' }]
    case 'youDied':
      return [{ text: t('log.youDied', { name: s('name') }), tone: 'bad' }]
    case 'youDiedEnv':
      return [{ text: t('log.youDiedEnv'), tone: 'bad' }]
    case 'keyTaken':
      return [{ text: t('log.keyDrop', { name: s('name') }), tone: 'good' }]
    case 'targetGone':
      return [{ text: t('log.targetGone'), tone: 'normal' }]
    case 'fled':
      return [{ text: t('log.fled'), tone: 'normal' }]
    case 'special':
      return [{ text: t('log.special'), tone: 'good' }]
    case 'boonOffer':
      return [{ text: t('log.boonOffer'), tone: 'good' }]
    case 'boonTaken':
      return [{ text: t('log.boonTaken', { name: boonName(s('id')) }), tone: 'good' }]
    case 'playerExchange':
      return localizeAtt(ev.r as BEResult)
    case 'defend':
      return localizeDef(ev.r as BEResult)
    default:
      return []
  }
}

export function localizeBackendEvents(events: BackendEvent[]): BackendLogLine[] {
  const out: BackendLogLine[] = []
  for (const ev of events) out.push(...localizeBackendEvent(ev))
  return out
}

/** One-line localized summary of an authoritative backend match state. */
export function summarizeBackendState(s: BackendState): BackendLogLine {
  return {
    text: t('log.serverState', { alive: s.alive, total: s.total, step: s.step, rank: s.rank }),
    tone: 'system',
  }
}
